import { useState } from 'react';
import { useRouter } from 'next/router';
import { cloneDeep } from 'es-toolkit';

import {
  Accordion,
  Anchor,
  Box,
  Checkbox,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Select,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyEditV2WebApiHandler_modularListingProgress,
  useRentalManagerUserPropertySummaryWebApiHandler_applications,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useConvertSingleFamilyToRoomForRent } from '../../hooks/queries/useConvertSingleFamilyToRoomForRent';

import PropertyConstants from '../../constants/PropertyConstants';
import { getDashboardOverviewRoute } from '../../constants/urls';

import RoomForRentCheckbox from '../modals/AddPropertyModal/components/RoomForRentCheckbox';

import type { ChangeEvent } from 'react';

const {
  PROPERTY_TYPES: { CONDO, HOUSE, TOWNHOUSE },
  LISTING_CODES_BY_TYPE,
  LISTING_TYPES,
} = PropertyConstants;

export type CorrectPropertyTypes = typeof CONDO | typeof HOUSE | typeof TOWNHOUSE;

type Props = {
  listingAlias: string;
  correctPropertyType: CorrectPropertyTypes;
  onSelectChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  isRoomForRent: boolean;
  onConvertComplete?: () => void;
};

const EditPropertyType = ({
  listingAlias,
  correctPropertyType,
  onSelectChange,
  isRoomForRent,
  onConvertComplete = () => {},
}: Props) => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const router = useRouter();

  const [acknowledged, setAcknowledged] = useState(false);

  const { mutate: convertSingleFamilyToRoomForRent } = useConvertSingleFamilyToRoomForRent();

  const { mutate: modularListingProgressMutate } =
    useRentalManagerUserPropertyEditV2WebApiHandler_modularListingProgress();

  const [accordionData, setAccordionData] = useState([
    {
      title: 'Need to list an entire apartment community?',
      content:
        'If this property always has availability, you can list it as an apartment community. If you’d prefer to list units individually, create a separate property for each unit.',
      linkText: 'Options for advertising multifamily properties',
      linkHref: 'https://www.zillow.com/z/rental-property-advertising/',
    },
    {
      title: 'Published listing shows wrong property type?',
      content:
        'If you’ve set the correct property type in Zillow Rental Manager, but the published listing still shows the wrong type, the type you selected may have been overwritten by public records data.',
    },
    {
      condition: !isRoomForRent,
      title: 'Need to list this property as a Room for Rent?',
      content:
        'Room for Rent listings don’t have access to Applications, Leases, and Payments or our Premium offerings and won’t display on Trulia.',
      acknowledgement: 'I understand this action can’t be undone.',
      linkText: 'Convert this property to a Room for Rent',
      action: () => {
        convertSingleFamilyToRoomForRent(
          { postData: { listingAlias } },
          {
            onSuccess: ({
              isConverted,
              listingAlias: convertedListingAlias,
            }: {
              isConverted: boolean;
              listingAlias: string;
            }) => {
              queryClient.invalidateQueries({
                queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
              });
              queryClient.invalidateQueries({
                queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias }),
              });

              enqueueToast(<Toast tone="success">Property converted to room for rent</Toast>);

              if (isConverted) {
                modularListingProgressMutate({
                  postData: {
                    listingAlias: convertedListingAlias,
                    step: LISTING_TYPES.ROOM.toLowerCase().replace(/ /g, '_'),
                  },
                });

                if (convertedListingAlias !== listingAlias) {
                  router.push(getDashboardOverviewRoute({ listingAlias: convertedListingAlias }));
                  return;
                }
                queryClient.invalidateQueries({
                  queryKey: useRentalManagerUserPropertySummaryWebApiHandler_listing.getKey({ listingAlias }),
                });
                return onConvertComplete();
              }
            },
            onError: (error) => {
              const message = (error as { error?: { message?: string } })?.error?.message ?? 'An error occurred';
              const accordionCopy = cloneDeep(accordionData);
              accordionCopy[2].error = `There was an error with converting your property. Message: ${message}`;
              setAccordionData(accordionCopy);
            },
          },
        );
      },
      error: '',
    },
  ]);

  return (
    <div>
      <FormField css={{ marginBlock: '300' }}>
        <Label>Property type</Label>
        <Select
          name="propertyType"
          aria-label="Property type"
          value={correctPropertyType}
          onChange={onSelectChange}
          css={{ whiteSpace: 'pre' }}
        >
          <option value={HOUSE}>{HOUSE}</option>
          <option value={CONDO}>{CONDO}</option>
          <option value={TOWNHOUSE}>{TOWNHOUSE}</option>
        </Select>
      </FormField>

      {isRoomForRent && <RoomForRentCheckbox isRoomForRentChecked={isRoomForRent} isRoomForRentDisabled={true} />}

      {accordionData.map((dataPoint, index) => {
        if (dataPoint.condition !== undefined ? dataPoint.condition : true) {
          return (
            <Box key={`accordionData-${dataPoint.title}`} css={{ marginBottom: '200' }}>
              <Accordion.Root>
                <Accordion.Item value={`edit-property-type-${index}`}>
                  <Accordion.Header>{dataPoint.title}</Accordion.Header>
                  <Accordion.Panel>
                    <Text asChild>
                      <p>
                        {dataPoint.content}
                        {dataPoint.acknowledgement && (
                          <LabeledControl
                            label={<Label>{dataPoint.acknowledgement}</Label>}
                            control={
                              <Checkbox checked={acknowledged} onChange={() => setAcknowledged(!acknowledged)} />
                            }
                            css={{ marginBlock: '200' }}
                          />
                        )}
                        {dataPoint.linkText && dataPoint.linkHref && (
                          <Anchor
                            href={dataPoint.linkHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            css={{ display: 'inline-block', marginBlock: 'default' }}
                          >
                            {dataPoint.linkText}
                          </Anchor>
                        )}
                      </p>
                    </Text>
                    {dataPoint.linkText && dataPoint.action && (
                      <FormField
                        error={Boolean(dataPoint.error)}
                        description={dataPoint.error ? <FormHelp>{dataPoint.error}</FormHelp> : undefined}
                      >
                        <TextButton
                          disabled={Boolean(dataPoint.acknowledgement && !acknowledged)}
                          textStyle="body-sm"
                          css={{ marginBlock: '200' }}
                          onClick={dataPoint.action}
                        >
                          {dataPoint.linkText}
                        </TextButton>
                      </FormField>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            </Box>
          );
        }
        return null;
      })}
    </div>
  );
};

export default EditPropertyType;
